import {
  getParentToolCallIdFromPart,
  getToolCallIdFromPart,
  hasVisibleToolOutput,
  isCodeAnalyzerToolPart,
  isRunningPart,
} from './chat-message-part-accessors';
import {
  getHeadlineKindForPart,
  isInnerClaudeActivityPart,
  partitionConsolidatableParts,
} from './chat-message-list-consolidation';
import type { ChatRenderUnit, RenderablePart } from './chat-message-list-types';
import {
  headlineKindForSurface,
  resolveWorkHeadlineKind,
  type ChatHeadlineKind,
} from './chat-headline-actions';

export function isReasoningRenderablePart(item: RenderablePart): boolean {
  return (
    item.processedPart.kind === 'assistant-text' &&
    item.processedPart.part.type === 'reasoning'
  );
}

export function expandSubAgentChildUnits(
  units: ChatRenderUnit[],
): ChatRenderUnit[] {
  return units.flatMap((unit) => {
    if (unit.type !== 'analyzer-group') {
      return [unit];
    }

    if (unit.children.length === 0) {
      return [unit];
    }

    return partitionConsolidatableParts(unit.children);
  });
}

export type SubAgentVisualSegment = {
  type: 'activity';
  kind: ChatHeadlineKind;
  units: ChatRenderUnit[];
  key: string;
};

function isThoughtUnit(
  unit: ChatRenderUnit,
): unit is Extract<ChatRenderUnit, { type: 'single' }> {
  return (
    unit.type === 'single' && unit.item.processedPart.kind === 'assistant-text'
  );
}

function activitySegmentKey({
  parentKey,
  kind,
  segments,
}: {
  parentKey: string;
  kind: ChatHeadlineKind;
  segments: SubAgentVisualSegment[];
}): string {
  const ordinal = segments.filter((segment) => segment.kind === kind).length;

  return `activity-${parentKey}-${kind}-${ordinal}`;
}

function getWorkHeadlineKindForUnit({
  unit,
  parentKind,
}: {
  unit: ChatRenderUnit;
  parentKind: ChatHeadlineKind;
}): ChatHeadlineKind {
  const unitKind =
    unit.type === 'consolidated'
      ? headlineKindForSurface(unit.surface)
      : unit.type === 'analyzer-group'
        ? 'analyze-source'
        : getHeadlineKindForPart(unit.item.processedPart);

  return resolveWorkHeadlineKind({ unitKind, parentKind });
}

export function splitSubAgentVisualTimeline({
  units,
  parentKind = 'analyze-source',
  parentKey,
}: {
  units: ChatRenderUnit[];
  parentKind?: ChatHeadlineKind;
  parentKey: string;
}): SubAgentVisualSegment[] {
  const segments: SubAgentVisualSegment[] = [];
  let activity: ChatRenderUnit[] = [];
  let activityKind: ChatHeadlineKind | undefined;
  let pendingThoughts: ChatRenderUnit[] = [];

  const flushActivity = () => {
    if (activity.length === 0 || !activityKind) {
      return;
    }

    segments.push({
      type: 'activity',
      kind: activityKind,
      units: activity,
      key: activitySegmentKey({ parentKey, kind: activityKind, segments }),
    });
    activity = [];
    activityKind = undefined;
  };

  const takePendingThoughtsAsParentJob = () => {
    if (pendingThoughts.length === 0) {
      return;
    }

    if (activityKind && activityKind !== parentKind) {
      flushActivity();
    }

    activity.push(...pendingThoughts);
    pendingThoughts = [];
    activityKind = parentKind;
  };

  for (const unit of units) {
    if (isThoughtUnit(unit)) {
      pendingThoughts.push(unit);
      continue;
    }

    const kind = getWorkHeadlineKindForUnit({ unit, parentKind });

    if (
      pendingThoughts.length > 0 &&
      kind !== parentKind &&
      activityKind === parentKind
    ) {
      takePendingThoughtsAsParentJob();
    }

    if (activity.length > 0 && kind !== activityKind) {
      flushActivity();
    }

    activity.push(...pendingThoughts);
    pendingThoughts = [];
    activity.push(unit);
    activityKind = kind;
  }

  if (pendingThoughts.length > 0 && activityKind) {
    takePendingThoughtsAsParentJob();
  }

  flushActivity();

  return segments;
}

export function withRunningWorkTail({
  segments,
  isRunning,
  parentKind,
  parentKey,
}: {
  segments: SubAgentVisualSegment[];
  isRunning: boolean;
  parentKind: ChatHeadlineKind;
  parentKey: string;
}): SubAgentVisualSegment[] {
  const last = segments.at(-1);

  if (!isRunning || segments.length === 0 || last?.kind === parentKind) {
    return segments;
  }

  return [
    ...segments,
    {
      type: 'activity',
      kind: parentKind,
      units: [],
      key: activitySegmentKey({ parentKey, kind: parentKind, segments }),
    },
  ];
}

export function timelineWithParentHeadline({
  segments,
  parentKind,
  parentKey,
  units,
}: {
  segments: SubAgentVisualSegment[];
  parentKind: ChatHeadlineKind;
  parentKey: string;
  units: ChatRenderUnit[];
}): SubAgentVisualSegment[] {
  if (parentKind === 'passthrough') {
    return segments;
  }

  if (segments.length === 0) {
    return [
      {
        type: 'activity',
        kind: parentKind,
        units,
        key: activitySegmentKey({ parentKey, kind: parentKind, segments }),
      },
    ];
  }

  if (segments.some((segment) => segment.kind === parentKind)) {
    return segments;
  }

  return [
    ...segments,
    {
      type: 'activity',
      kind: parentKind,
      units: [],
      key: activitySegmentKey({ parentKey, kind: parentKind, segments }),
    },
  ];
}

export function ensureBackgroundRunningHeadline({
  segments,
  forceParentRunning,
  parentKind,
  parentKey,
}: {
  segments: SubAgentVisualSegment[];
  forceParentRunning: boolean;
  parentKind: ChatHeadlineKind;
  parentKey: string;
}): SubAgentVisualSegment[] {
  if (
    !forceParentRunning ||
    parentKind === 'passthrough' ||
    segments.some((segment) => segment.kind === parentKind)
  ) {
    return segments;
  }

  return [
    ...segments,
    {
      type: 'activity',
      kind: parentKind,
      units: [],
      key: activitySegmentKey({ parentKey, kind: parentKind, segments }),
    },
  ];
}

export function collectPartsFromRenderUnits(
  units: ChatRenderUnit[],
): RenderablePart[] {
  return units.flatMap((unit) => {
    if (unit.type === 'consolidated') {
      return unit.items;
    }

    if (unit.type === 'analyzer-group') {
      return [unit.parent, ...unit.children];
    }

    return [unit.item];
  });
}

function shouldHideEmptyAnalyzer({
  analyzer,
  inners,
}: {
  analyzer: RenderablePart;
  inners: RenderablePart[];
}): boolean {
  return (
    inners.length === 0 &&
    !isRunningPart(analyzer.processedPart) &&
    !hasVisibleToolOutput(analyzer.processedPart)
  );
}

type OpenAnalyzerGroup = {
  parent: RenderablePart;
  inners: RenderablePart[];
};

type PartitionSlot =
  | { kind: 'analyzer'; id: string }
  | { kind: 'reasoning'; item: RenderablePart }
  | { kind: 'parts'; items: RenderablePart[] };

function analyzerToolCallId(item: RenderablePart): string {
  return getToolCallIdFromPart(item.processedPart) ?? item.processedPart.key;
}

function isAnalyzerChildPart(item: RenderablePart): boolean {
  return (
    isInnerClaudeActivityPart(item.processedPart) ||
    isReasoningRenderablePart(item)
  );
}

export function partitionSubAgentChildParts(
  parts: RenderablePart[],
): ChatRenderUnit[] {
  const units: ChatRenderUnit[] = [];
  const analyzers = new Map<string, OpenAnalyzerGroup>();
  const slots: PartitionSlot[] = [];
  let lastAnalyzerId: string | undefined;

  const appendParts = (item: RenderablePart) => {
    const last = slots.at(-1);

    if (last?.kind === 'parts') {
      last.items.push(item);

      return;
    }

    slots.push({ kind: 'parts', items: [item] });
  };

  const emitAnalyzer = (id: string) => {
    const group = analyzers.get(id);

    if (!group) {
      return;
    }

    analyzers.delete(id);

    if (
      shouldHideEmptyAnalyzer({
        analyzer: group.parent,
        inners: group.inners,
      })
    ) {
      return;
    }

    units.push({
      type: 'analyzer-group',
      parent: group.parent,
      children: group.inners,
      key: `analyzer-${group.parent.processedPart.key}`,
    });
  };

  for (const item of parts) {
    if (isCodeAnalyzerToolPart(item.processedPart)) {
      const id = analyzerToolCallId(item);
      const existing = analyzers.get(id);

      if (existing) {
        existing.parent = item;
      } else {
        analyzers.set(id, { parent: item, inners: [] });
        slots.push({ kind: 'analyzer', id });
      }

      lastAnalyzerId = id;
      continue;
    }

    const parentFromPart = getParentToolCallIdFromPart(item.processedPart);

    if (parentFromPart) {
      const group = analyzers.get(parentFromPart);

      if (group) {
        group.inners.push(item);
        continue;
      }
    } else if (isAnalyzerChildPart(item) && lastAnalyzerId) {
      const group = analyzers.get(lastAnalyzerId);

      if (group) {
        group.inners.push(item);
        continue;
      }
    }

    if (isReasoningRenderablePart(item)) {
      slots.push({ kind: 'reasoning', item });
      continue;
    }

    appendParts(item);
  }

  for (const slot of slots) {
    if (slot.kind === 'analyzer') {
      emitAnalyzer(slot.id);
      continue;
    }

    if (slot.kind === 'reasoning') {
      units.push({ type: 'single', item: slot.item });
      continue;
    }

    units.push(...partitionConsolidatableParts(slot.items));
  }

  return units;
}
